import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AppConst from '../apis/appconst';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

/* ============================================================
 *  UHID SETTINGS — one shared sequence for the whole hospital
 *  Lab, OPD, IPD and Emergency all draw from the same counter.
 * ============================================================ */

const MODULE_COLORS = {
    LAB: '#fd7e14',
    OPD: '#198754',
    IPD: '#0d6efd',
    EMERGENCY: '#dc3545',
    UNKNOWN: '#6c757d',
};

const blankSettings = {
    prefix: 'UHID',
    separator: '',
    digits: 4,
    start_no: 1,
    next_no: 1,
    include_year: 0,
    year_format: 'yy',
    is_active: 1,
};

const UhidSettings = () => {

    const [activeTab, setActiveTab] = useState('settings');   // 'settings' | 'master'
    const [s, setS] = useState(blankSettings);
    const [meta, setMeta] = useState({ issued_count: 0, by_module: {} });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    // master registry
    const [master, setMaster] = useState([]);
    const [masterTotal, setMasterTotal] = useState(0);
    const [masterLoading, setMasterLoading] = useState(false);
    const [masterSearch, setMasterSearch] = useState('');
    const [masterModule, setMasterModule] = useState('');

    const getStoredUser = () => {
        try {
            const raw = sessionStorage.getItem('ldata') || localStorage.getItem('ldata');
            if (raw) return JSON.parse(raw);
        } catch (e) { /* ignore */ }
        return null;
    };

    /* ============================================================
     *  PREVIEW — same rule as uhid_build() on the backend
     * ============================================================ */
    const buildPreview = useCallback((cfg, seqOverride = null) => {
        const prefix = String(cfg.prefix || '').trim();
        if (!prefix) return '—';

        const sep = String(cfg.separator || '');
        const digits = Math.max(1, Math.min(12, Number(cfg.digits || 4)));
        const seq = seqOverride !== null
            ? seqOverride
            : Math.max(Number(cfg.next_no || 1), Number(cfg.start_no || 1), 1);

        const num = String(seq).padStart(digits, '0');

        let yearPart = '';
        if (Number(cfg.include_year) === 1) {
            const y = new Date().getFullYear();
            yearPart = (cfg.year_format === 'yyyy' ? String(y) : String(y).slice(-2)) + sep;
        }

        return `${prefix}${sep}${yearPart}${num}`;
    }, []);

    const nextSeq = Math.max(Number(s.next_no || 1), Number(s.start_no || 1), 1);

    /* ============================================================
     *  LOAD
     * ============================================================ */

    const loadSettings = async () => {
        setLoading(true);
        try {
            const res = await axios.post(
                AppConst.BASE_URL + AppConst.getUhidSettingsUrl,
                new FormData(),
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            if (res?.data?.status && res.data.record) {
                const r = res.data.record;
                setS({
                    prefix: r.prefix ?? 'UHID',
                    separator: r.separator ?? '',
                    digits: Number(r.digits || 4),
                    start_no: Number(r.start_no || 1),
                    next_no: Number(r.next_no || 1),
                    include_year: Number(r.include_year || 0),
                    year_format: r.year_format || 'yy',
                    is_active: Number(r.is_active || 0),
                });
                setMeta({
                    issued_count: Number(r.issued_count || 0),
                    by_module: r.by_module || {},
                });
                setDirty(false);
            } else {
                toast.error(res?.data?.message || 'Could not load UHID settings');
            }
        } catch (err) {
            console.error('loadSettings error', err);
            toast.error('Could not load UHID settings');
        } finally {
            setLoading(false);
        }
    };

    const loadMaster = async () => {
        setMasterLoading(true);
        try {
            const fd = new FormData();
            fd.append('page_no', 'none');
            if (masterSearch.trim()) fd.append('search_key', masterSearch.trim());
            if (masterModule) fd.append('module_key', masterModule);

            const res = await axios.post(
                AppConst.BASE_URL + AppConst.getUhidMasterUrl, fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            if (res?.data?.status) {
                setMaster(res.data.records || []);
                setMasterTotal(Number(res.data.total || 0));
            } else {
                setMaster([]); setMasterTotal(0);
            }
        } catch (err) {
            console.error('loadMaster error', err);
            setMaster([]); setMasterTotal(0);
        } finally {
            setMasterLoading(false);
        }
    };

    useEffect(() => {
        loadSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (activeTab === 'master') loadMaster();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, masterModule]);

    /* ============================================================
     *  EDIT + SAVE
     * ============================================================ */

    const set = (field, value) => {
        setS(prev => ({ ...prev, [field]: value }));
        setDirty(true);
    };

    const handleSave = async () => {
        if (Number(s.is_active) === 1 && !String(s.prefix || '').trim()) {
            toast.error('A prefix is required while the central UHID is on');
            return;
        }

        const { isConfirmed } = await Swal.fire({
            title: 'Save UHID settings?',
            html: Number(s.is_active) === 1
                ? `<div style="text-align:left">
                     <p>The next patient registered — from <strong>any</strong> module —
                        will get:</p>
                     <p style="font-size:22px;color:#405189;font-weight:700;text-align:center">
                        ${buildPreview(s)}
                     </p>
                     <p style="font-size:13px;color:#666">
                        then ${buildPreview(s, nextSeq + 1)}, ${buildPreview(s, nextSeq + 2)} …
                     </p>
                   </div>`
                : `<p>Central UHID will be <strong>off</strong>. Every module goes back to
                      its own numbering (LAB.., OP.., IPD.., EMR..).</p>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, save',
        });
        if (!isConfirmed) return;

        setSaving(true);
        try {
            const u = getStoredUser();
            const fd = new FormData();
            fd.append('prefix', s.prefix || '');
            fd.append('separator', s.separator || '');
            fd.append('digits', s.digits);
            fd.append('start_no', s.start_no);
            fd.append('next_no', s.next_no);
            fd.append('include_year', s.include_year);
            fd.append('year_format', s.year_format);
            fd.append('is_active', s.is_active);
            if (u) {
                fd.append('user_id', u.user_id || '');
                fd.append('user_name', `${u.fname || ''} ${u.lname || ''}`.trim());
            }

            const res = await axios.post(
                AppConst.BASE_URL + AppConst.saveUhidSettingsUrl, fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            if (res?.data?.status) {
                toast.success(res.data.message || 'UHID settings saved');
                if (res.data.warning) toast.warn(res.data.warning);
                loadSettings();
            } else {
                toast.error(res?.data?.message || 'Save failed');
            }
        } catch (err) {
            console.error('save error', err);
            toast.error('Save failed');
        } finally {
            setSaving(false);
        }
    };

    const on = Number(s.is_active) === 1;

    /* ============================================================
     *  RENDER
     * ============================================================ */

    return (
        <div className="page-content">
            <div className="container-fluid">

                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 style={{ color: '#405189', margin: 0 }}>
                        <i className="ri-fingerprint-line me-2"></i>UHID Settings
                    </h4>
                    {activeTab === 'settings' && (
                        <div className="d-flex gap-2">
                            <button className="btn btn-soft-primary btn-sm" onClick={loadSettings} disabled={loading}>
                                <i className="ri-refresh-line me-1"></i> Reload
                            </button>
                            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || loading}>
                                {saving
                                    ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving...</>
                                    : <><i className="ri-save-line me-1"></i>Save Settings</>}
                            </button>
                        </div>
                    )}
                </div>

                {/* TABS */}
                <ul className="nav nav-tabs mb-3">
                    <li className="nav-item">
                        <button className={`nav-link ${activeTab === 'settings' ? 'active fw-bold' : ''}`}
                            onClick={() => setActiveTab('settings')}>
                            <i className="ri-settings-3-line me-1"></i> Numbering
                            {dirty && <span className="badge bg-warning text-dark ms-2">unsaved</span>}
                        </button>
                    </li>
                    <li className="nav-item">
                        <button className={`nav-link ${activeTab === 'master' ? 'active fw-bold' : ''}`}
                            onClick={() => setActiveTab('master')}>
                            <i className="ri-list-check-2 me-1"></i> UHID Master
                            {meta.issued_count > 0 && <span className="badge bg-primary ms-2">{meta.issued_count}</span>}
                        </button>
                    </li>
                </ul>

                {/* ============================ SETTINGS ============================ */}
                {activeTab === 'settings' && (
                    <>
                        <div className="alert alert-info py-2" style={{ fontSize: 13 }}>
                            <i className="ri-information-line me-1"></i>
                            <strong>One number series for the whole hospital.</strong> Lab, OPD, IPD and
                            Emergency all take the next number from the same counter — register in Lab and
                            you get {buildPreview(s)}, the next OPD patient gets {buildPreview(s, nextSeq + 1)},
                            and so on. The UHID is saved into that module's own table as usual.
                        </div>

                        {loading ? (
                            <div className="card"><div className="card-body text-center py-5">
                                <div className="spinner-border text-primary"></div>
                            </div></div>
                        ) : (
                        <div className="row g-3">

                            {/* ---------- CONFIG ---------- */}
                            <div className="col-lg-7">
                                <div className="card h-100">
                                    <div className="card-body">

                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <h6 className="mb-0" style={{ color: '#405189' }}>Number Format</h6>
                                            <div className="form-check form-switch">
                                                <input className="form-check-input" type="checkbox"
                                                    id="uhidActive"
                                                    checked={on}
                                                    onChange={(e) => set('is_active', e.target.checked ? 1 : 0)}
                                                    style={{ cursor: 'pointer' }} />
                                                <label className="form-check-label" htmlFor="uhidActive" style={{ fontSize: 13 }}>
                                                    {on ? 'On' : 'Off'}
                                                </label>
                                            </div>
                                        </div>

                                        <div className="row g-3">
                                            <div className="col-md-4">
                                                <label className="form-label">Prefix <span className="text-danger">*</span></label>
                                                <input type="text" className="form-control"
                                                    value={s.prefix}
                                                    maxLength={20}
                                                    disabled={!on}
                                                    placeholder="UHID"
                                                    onChange={(e) => set('prefix', e.target.value.toUpperCase())} />
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label">Separator</label>
                                                <input type="text" className="form-control"
                                                    value={s.separator}
                                                    maxLength={5}
                                                    disabled={!on}
                                                    placeholder="none"
                                                    onChange={(e) => set('separator', e.target.value)} />
                                                <small className="text-muted" style={{ fontSize: 11 }}>
                                                    blank, or something like - or /
                                                </small>
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label">Digits</label>
                                                <input type="number" className="form-control"
                                                    value={s.digits} min={1} max={12} disabled={!on}
                                                    onChange={(e) => set('digits', Math.max(1, Math.min(12, Number(e.target.value || 1))))} />
                                                <small className="text-muted" style={{ fontSize: 11 }}>
                                                    4 → 0034
                                                </small>
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label">Include Year</label>
                                                <div className="d-flex gap-2 align-items-center">
                                                    <div className="form-check form-switch mb-0">
                                                        <input className="form-check-input" type="checkbox"
                                                            checked={Number(s.include_year) === 1}
                                                            disabled={!on}
                                                            onChange={(e) => set('include_year', e.target.checked ? 1 : 0)} />
                                                    </div>
                                                    <select className="form-select"
                                                        value={s.year_format}
                                                        disabled={!on || Number(s.include_year) !== 1}
                                                        onChange={(e) => set('year_format', e.target.value)}>
                                                        <option value="yy">26</option>
                                                        <option value="yyyy">2026</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label">Start No</label>
                                                <input type="number" className="form-control"
                                                    value={s.start_no} min={1} disabled={!on}
                                                    onChange={(e) => set('start_no', Math.max(1, Number(e.target.value || 1)))} />
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label">Next No</label>
                                                <input type="number" className="form-control"
                                                    value={s.next_no} min={1} disabled={!on}
                                                    onChange={(e) => set('next_no', Math.max(1, Number(e.target.value || 1)))} />
                                                <small className="text-muted" style={{ fontSize: 11 }}>
                                                    the running counter
                                                </small>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>

                            {/* ---------- PREVIEW ---------- */}
                            <div className="col-lg-5">
                                <div className="card h-100">
                                    <div className="card-body">
                                        <h6 className="mb-3" style={{ color: '#405189' }}>Preview</h6>

                                        {on ? (
                                            <>
                                                <div className="text-center p-3 mb-3"
                                                    style={{ background: '#f1f5fb', borderRadius: 8 }}>
                                                    <div className="text-muted" style={{ fontSize: 11 }}>NEXT PATIENT GETS</div>
                                                    <div style={{ fontSize: 30, fontWeight: 700, color: '#405189', letterSpacing: 1 }}>
                                                        {buildPreview(s)}
                                                    </div>
                                                </div>

                                                <div style={{ fontSize: 13 }}>
                                                    <div className="text-muted mb-2">then, whoever registers next:</div>
                                                    {[1, 2, 3].map(i => {
                                                        const mods = ['OPD', 'IPD', 'LAB'];
                                                        return (
                                                            <div key={i} className="d-flex justify-content-between align-items-center border-bottom py-1">
                                                                <span className="badge" style={{ background: MODULE_COLORS[mods[i - 1]] }}>
                                                                    {mods[i - 1]}
                                                                </span>
                                                                <code style={{ fontSize: 14, color: '#405189' }}>
                                                                    {buildPreview(s, nextSeq + i)}
                                                                </code>
                                                            </div>
                                                        );
                                                    })}
                                                    <div className="text-muted mt-2" style={{ fontSize: 11 }}>
                                                        The module doesn't change the number — it's one shared queue.
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="text-center text-muted py-4">
                                                <i className="ri-close-circle-line" style={{ fontSize: 34, color: '#ccc' }}></i>
                                                <div className="mt-2" style={{ fontSize: 13 }}>
                                                    Central UHID is off.<br />
                                                    Each module uses its own numbering<br />
                                                    (LAB.., OP.., IPD.., EMR..).
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* ---------- ISSUED SO FAR ---------- */}
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-body py-3">
                                        <div className="d-flex flex-wrap align-items-center gap-4">
                                            <div>
                                                <div className="text-muted" style={{ fontSize: 11 }}>TOTAL UHIDs</div>
                                                <div style={{ fontSize: 20, fontWeight: 700, color: '#405189' }}>
                                                    {meta.issued_count}
                                                </div>
                                            </div>
                                            {Object.keys(meta.by_module || {}).length > 0 && (
                                                <>
                                                    <div style={{ width: 1, height: 36, background: '#dee2e6' }}></div>
                                                    {Object.entries(meta.by_module).map(([mod, cnt]) => (
                                                        <div key={mod}>
                                                            <span className="badge" style={{ background: MODULE_COLORS[mod] || '#6c757d', fontSize: 10 }}>
                                                                {mod}
                                                            </span>
                                                            <div style={{ fontSize: 15, fontWeight: 600 }}>{cnt}</div>
                                                        </div>
                                                    ))}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        )}

                        <div className="alert alert-warning mt-3 py-2" style={{ fontSize: 13 }}>
                            <i className="ri-alert-line me-1"></i>
                            <strong>Next No</strong> is the live counter. Lowering it makes the generator step
                            forward over numbers already taken until it finds a free one — you can't create a
                            duplicate, but the next save gets slow. Raise it freely to leave a gap.
                            Changing the <strong>prefix</strong> or <strong>digits</strong> only affects new
                            UHIDs; existing patients keep the number they already have.
                        </div>
                    </>
                )}

                {/* ============================ MASTER ============================ */}
                {activeTab === 'master' && (
                    <div className="card">
                        <div className="card-body">
                            <div className="row g-2 mb-3">
                                <div className="col-md-4">
                                    <div className="input-group input-group-sm">
                                        <span className="input-group-text"><i className="ri-search-line"></i></span>
                                        <input type="text" className="form-control"
                                            placeholder="UHID / patient name / mobile"
                                            value={masterSearch}
                                            onChange={(e) => setMasterSearch(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') loadMaster(); }} />
                                        <button className="btn btn-primary" onClick={loadMaster}>Search</button>
                                    </div>
                                </div>
                                <div className="col-md-2">
                                    <select className="form-select form-select-sm"
                                        value={masterModule}
                                        onChange={(e) => setMasterModule(e.target.value)}>
                                        <option value="">Issued by: all</option>
                                        <option value="LAB">LAB</option>
                                        <option value="OPD">OPD</option>
                                        <option value="IPD">IPD</option>
                                        <option value="EMERGENCY">EMERGENCY</option>
                                    </select>
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    <small className="text-muted">
                                        {masterTotal} UHID{masterTotal !== 1 ? 's' : ''} issued
                                    </small>
                                </div>
                            </div>

                            <div className="table-responsive" style={{ maxHeight: '60vh' }}>
                                <table className="table table-sm table-bordered table-hover mb-0" style={{ fontSize: 13 }}>
                                    <thead className="table-dark sticky-top">
                                        <tr>
                                            <th style={{ width: 60 }}>#</th>
                                            <th style={{ width: 150 }}>UHID</th>
                                            <th>Patient Name</th>
                                            <th style={{ width: 140 }}>Mobile</th>
                                            <th style={{ width: 100 }}>Gender</th>
                                            <th style={{ width: 130 }}>Issued By</th>
                                            <th style={{ width: 110 }}>Source ID</th>
                                            <th style={{ width: 180 }}>Created</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {masterLoading ? (
                                            <tr><td colSpan={8} className="text-center py-4">
                                                <div className="spinner-border text-primary"></div>
                                            </td></tr>
                                        ) : master.length === 0 ? (
                                            <tr><td colSpan={8} className="text-center py-4 text-muted">
                                                No UHIDs found
                                            </td></tr>
                                        ) : master.map((m, i) => (
                                            <tr key={m.uhid_id || i}>
                                                <td>{i + 1}</td>
                                                <td><strong className="text-primary">{m.uhid}</strong></td>
                                                <td>{m.patient_name || <span className="text-muted">—</span>}</td>
                                                <td>{m.patient_mobile || '—'}</td>
                                                <td>{m.patient_gender || '—'}</td>
                                                <td>
                                                    {m.source_module
                                                        ? <span className="badge" style={{ background: MODULE_COLORS[m.source_module] || '#6c757d' }}>
                                                            {m.source_module}
                                                          </span>
                                                        : '—'}
                                                </td>
                                                <td>{m.source_id || '—'}</td>
                                                <td>{m.created_at || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default UhidSettings;